import React, { useState } from "react";
import { Button } from "../ui/button";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, AlertTriangle, Save } from "lucide-react";
import DateField from "../DateField";

const field = "w-full mt-1.5 rounded-lg bg-slate-50 border border-slate-300 px-3 py-2.5 text-base sm:text-sm text-slate-900 focus:border-sky-200 focus:outline-none";

export const TargetBlock = ({ state, patch }) => {
  const bp = state.blueprint;
  const [form, setForm] = useState({
    target_amount: bp?.target_amount || 10000,
    target_currency: bp?.target_currency || "INR",
    target_date: bp?.target_date || "",
    selected_combination: bp?.selected_combination ?? 0,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const symbol = form.target_currency === "USD" ? "$" : "₹";

  const save = async (next = form) => {
    setSaving(true);
    try {
      const { data } = await api.put("/accelerator/blueprint", {
        target_amount: Number(next.target_amount),
        target_currency: next.target_currency,
        target_date: next.target_date || null,
        selected_combination: Number(next.selected_combination),
      });
      patch({ blueprint: data.blueprint, progress: data.progress });
      setForm((f) => ({ ...f, selected_combination: data.blueprint.selected_combination ?? 0 }));
      toast.success("Target saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save your target");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-sky-200 bg-sky-50 p-5 space-y-4" data-testid="accelerator-target-block">
      <div>
        <h3 className="font-bold text-slate-900">Your first validation target</h3>
        <p className="text-xs text-slate-600 mt-1">A target to test against — never a promise or a prediction of income.</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <label className="block text-xs text-slate-500">Target additional income
          <input type="number" min={1000} step={500} value={form.target_amount} onChange={(e) => set("target_amount", e.target.value)} className={field} data-testid="blueprint-input-target-amount" />
        </label>
        <label className="block text-xs text-slate-500">Currency
          <select value={form.target_currency} onChange={(e) => set("target_currency", e.target.value)} className={field} data-testid="blueprint-input-currency">
            <option value="INR">₹ INR</option>
            <option value="USD">$ USD</option>
          </select>
        </label>
        <label className="block text-xs text-slate-500">Target date (optional)
          <DateField value={form.target_date} onChange={(v) => set("target_date", v)} testId="blueprint-target-date" placeholder="Choose a date" />
        </label>
      </div>

      {bp?.target_realistic === false && bp?.target_reality_note && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4" data-testid="blueprint-reality-flag">
          <p className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-700">
            <AlertTriangle className="h-3.5 w-3.5" /> Honest reality check
          </p>
          <p className="mt-1.5 text-xs leading-relaxed text-rose-800">{bp.target_reality_note}</p>
        </div>
      )}

      {!!bp?.target_combinations?.length && (
        <div data-testid="blueprint-combinations">
          <p className="text-xs text-slate-600">
            Ways you could reach {symbol}{Number(bp.validation_amount ?? form.target_amount).toLocaleString()}
            {bp.target_realistic === false ? " in your first test" : ""}
          </p>
          <div className="mt-2 grid gap-2 sm:grid-cols-3">
            {bp.target_combinations.map((c, i) => (
              <button key={i} onClick={() => { set("selected_combination", i); save({ ...form, selected_combination: i }); }} data-testid={`blueprint-combination-${i}`}
                className={`rounded-lg border px-3 py-3 text-left text-sm transition-colors ${Number(form.selected_combination) === i ? "border-sky-300 bg-white text-sky-700" : "border-slate-300 bg-white/70 text-slate-700 hover:border-slate-400"}`}>
                <span className="font-semibold">{c.customers} × {symbol}{Number(c.price_each).toLocaleString()}</span>
                <span className="block text-xs text-slate-500 mt-1">adds up to {symbol}{Number(c.total || c.customers * c.price_each).toLocaleString()}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <Button onClick={() => save()} disabled={saving} className="h-11 bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="target-save-button">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Save target
      </Button>
    </div>
  );
};

export default TargetBlock;
